// ERZEUGT aus lib/knots/rope.js durch scripts/gen-knot-twin.cjs — nicht von Hand ändern.
// Seilmodell und Zeichner des Knoten-Lexikons. Rein (kein require, kein DOM): dieselbe Datei zeichnet auf dem
// Server (öffentliche Seiten) und — als erzeugter ESM-Zwilling src/shared/knots/rope.js — im Browser (Spieler).
// Zwilling neu erzeugen: node scripts/gen-knot-twin.cjs
//
// Ein Knoten ist eine Menge von Strängen (Mittellinien aus Stützpunkten) plus Über/Unter je Kreuzung:
//   knot = { viewBox: [w,h], objects: [{type,x,y,w,h}],
//            strands: [{ id, points, tight?, moving, behind?: [[s0,s1]], workFrom? }],
//            over: boolean[], steps: number[] }
// Einheiten: `behind` und `workFrom` sind ANTEILE 0..1 der Stranglänge (bleiben gültig, wenn sich die Länge in der
// Tight-Phase ändert). `s`, `visibleTo` und Kreuzungs-`s` im Frame sind Bogenlängen in viewBox-Einheiten.
// `over[k]` gehört zur k-ten Kreuzung von findCrossings (sortiert nach dem späteren Durchgang b) und sagt, ob der
// spätere Durchgang b oben liegt (true) oder der frühere a (false) — so liest man es beim Nachfahren des Seils.
// Genau ein Strang bewegt sich (`moving`); ein zweiter bewegter Strang gilt als fest.

const BIND_END = 0.85; // mit `tight`: Binden bis hierher, danach Zuziehen
const PATCH = 6; // kleinste halbe Länge des Flickstücks über einer Kreuzung
const PATCH_MAX = 20; // größte halbe Länge (flache Kreuzungen)
const PATCH_EXT = 2; // Kern des Flickstücks ragt so weit über dessen Kante hinaus
const EDGE_W = 7, CORE_W = 4; // Strichbreiten (Darstellung; Farben kommen aus CSS)
const NEIGHBOUR = 3; // Segmente mit |i−j| < 3 im selben Strang zählen nie als Kreuzung

// ---------- Kurve ----------

function dedupe(points) {
  const out = [];
  for (const p of points || []) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p[0] - last[0], p[1] - last[1]) > 1e-9) out.push([p[0], p[1]]);
  }
  return out;
}

// Zentripetale Catmull-Rom (Barry-Goldman) zwischen p1 und p2 an Parameter t ∈ [0,1].
function crPoint(p0, p1, p2, p3, t) {
  const d = (a, b) => Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1])) || 1e-6;
  const t0 = 0, t1 = d(p0, p1), t2 = t1 + d(p1, p2), t3 = t2 + d(p2, p3);
  const tt = t1 + (t2 - t1) * t;
  const lerp = (a, b, ta, tb) => {
    const w = (tb - tt) / (tb - ta), v = (tt - ta) / (tb - ta);
    return [a[0] * w + b[0] * v, a[1] * w + b[1] * v];
  };
  const a1 = lerp(p0, p1, t0, t1), a2 = lerp(p1, p2, t1, t2), a3 = lerp(p2, p3, t2, t3);
  const b1 = lerp(a1, a2, t0, t2), b2 = lerp(a2, a3, t1, t3);
  return lerp(b1, b2, t1, t2);
}

// Gleichmäßig nach Bogenlänge abgetastete Kurve durch alle Stützpunkte.
function sampleStrand(points, step = 1) {
  const P = dedupe(points);
  if (P.length < 2) return { pts: P.length ? [P[0]] : [], s: P.length ? [0] : [], length: 0 };
  // Gespiegelte Hilfspunkte an den Enden: die Kurve läuft dort in Richtung des ersten/letzten Segments aus.
  const ext = [[2 * P[0][0] - P[1][0], 2 * P[0][1] - P[1][1]], ...P,
    [2 * P[P.length - 1][0] - P[P.length - 2][0], 2 * P[P.length - 1][1] - P[P.length - 2][1]]];
  const dense = [P[0]], dl = [0];
  for (let i = 1; i < ext.length - 2; i++) {
    const chord = Math.hypot(ext[i + 1][0] - ext[i][0], ext[i + 1][1] - ext[i][1]);
    const n = Math.max(4, Math.ceil(chord / step) * 3);
    for (let k = 1; k <= n; k++) {
      const q = k === n ? ext[i + 1] : crPoint(ext[i - 1], ext[i], ext[i + 1], ext[i + 2], k / n);
      const last = dense[dense.length - 1];
      dl.push(dl[dl.length - 1] + Math.hypot(q[0] - last[0], q[1] - last[1]));
      dense.push(q);
    }
  }
  const length = dl[dl.length - 1];
  const n = Math.max(1, Math.round(length / step));
  const h = length / n;
  const pts = [], s = [];
  let j = 0;
  for (let k = 0; k <= n; k++) {
    const target = k === n ? length : k * h;
    while (j < dl.length - 2 && dl[j + 1] < target) j++;
    const seg = dl[j + 1] - dl[j] || 1;
    const f = Math.min(1, Math.max(0, (target - dl[j]) / seg));
    pts.push([dense[j][0] + (dense[j + 1][0] - dense[j][0]) * f, dense[j][1] + (dense[j + 1][1] - dense[j][1]) * f]);
    s.push(target);
  }
  return { pts, s, length };
}

// ---------- Kreuzungen ----------

function segHit(p, p2, q, q2) {
  const rx = p2[0] - p[0], ry = p2[1] - p[1], sx = q2[0] - q[0], sy = q2[1] - q[1];
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;
  const qpx = q[0] - p[0], qpy = q[1] - p[1];
  const t = (qpx * sy - qpy * sx) / den, u = (qpx * ry - qpy * rx) / den;
  // halboffen [0,1): ein Treffer genau auf einem Stützpunkt zählt nur einmal
  if (t < 0 || t >= 1 || u < 0 || u >= 1) return null;
  return { t, u, x: p[0] + rx * t, y: p[1] + ry * t };
}

// Alle Kreuzungen zwischen und innerhalb der abgetasteten Stränge (Rastergitter statt aller Paare).
function findCrossings(strands) {
  const segs = [];
  strands.forEach((st, si) => {
    for (let i = 0; i < st.pts.length - 1; i++) segs.push([si, i]);
  });
  const cell = 8;
  const grid = new Map();
  const key = (cx, cy) => cx * 100003 + cy;
  for (let n = 0; n < segs.length; n++) {
    const [si, i] = segs[n], a = strands[si].pts[i], b = strands[si].pts[i + 1];
    const x0 = Math.floor(Math.min(a[0], b[0]) / cell), x1 = Math.floor(Math.max(a[0], b[0]) / cell);
    const y0 = Math.floor(Math.min(a[1], b[1]) / cell), y1 = Math.floor(Math.max(a[1], b[1]) / cell);
    for (let cx = x0; cx <= x1; cx++) for (let cy = y0; cy <= y1; cy++) {
      const k = key(cx, cy);
      let list = grid.get(k);
      if (!list) grid.set(k, (list = []));
      list.push(n);
    }
  }
  const out = [];
  for (const [k, list] of grid) {
    for (let x = 0; x < list.length; x++) for (let y = x + 1; y < list.length; y++) {
      const [sa, ia] = segs[list[x]], [sb, ib] = segs[list[y]];
      if (sa === sb && Math.abs(ia - ib) < NEIGHBOUR) continue;
      const A = strands[sa], B = strands[sb];
      const h = segHit(A.pts[ia], A.pts[ia + 1], B.pts[ib], B.pts[ib + 1]);
      if (!h) continue;
      // nur in der Zelle melden, in der der Schnittpunkt liegt (sonst doppelt aus Nachbarzellen)
      if (key(Math.floor(h.x / cell), Math.floor(h.y / cell)) !== k) continue;
      let p = { strand: sa, s: A.s[ia] + (A.s[ia + 1] - A.s[ia]) * h.t, i: ia };
      let q = { strand: sb, s: B.s[ib] + (B.s[ib + 1] - B.s[ib]) * h.u, i: ib };
      if (q.strand < p.strand || (q.strand === p.strand && q.s < p.s)) [p, q] = [q, p];
      out.push({ a: p, b: q, x: h.x, y: h.y });
    }
  }
  out.sort((m, n) => m.b.strand - n.b.strand || m.b.s - n.b.s);
  return out;
}

// ---------- Frame ----------

const cache = new WeakMap(); // knot → { sampled, crossings } der Ausgangsform `points`

function base(knot) {
  let c = cache.get(knot);
  if (!c) {
    const sampled = knot.strands.map((st) => sampleStrand(st.points));
    c = { sampled, crossings: findCrossings(sampled) };
    cache.set(knot, c);
  }
  return c;
}

const hasTight = (st) => Array.isArray(st.tight) && st.tight.length === (st.points || []).length;
const movingIndex = (knot) => knot.strands.findIndex((st) => st.moving);
const bindEnd = (knot) => (knot.strands.some(hasTight) ? BIND_END : 1);
const clamp01 = (u) => Math.min(1, Math.max(0, Number(u) || 0));

function sAt(st, s) {
  const { pts } = st, S = st.s;
  if (pts.length < 2) return { x: pts[0] ? pts[0][0] : 0, y: pts[0] ? pts[0][1] : 0, i: 0 };
  let lo = 0, hi = S.length - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (S[m] <= s) lo = m; else hi = m; }
  const f = Math.min(1, Math.max(0, (s - S[lo]) / ((S[hi] - S[lo]) || 1)));
  return { x: pts[lo][0] + (pts[hi][0] - pts[lo][0]) * f, y: pts[lo][1] + (pts[hi][1] - pts[lo][1]) * f, i: lo };
}

function frame(knot, u) {
  u = clamp01(u);
  const mi = movingIndex(knot);
  const be = bindEnd(knot);
  const zu = be < 1 && u > be ? (u - be) / (1 - be) : 0; // Anteil des Zuziehens
  const B = base(knot);
  const sampled = zu > 0
    ? knot.strands.map((st, i) => (hasTight(st)
      ? sampleStrand(st.points.map((p, k) => [p[0] + (st.tight[k][0] - p[0]) * zu, p[1] + (st.tight[k][1] - p[1]) * zu]))
      : B.sampled[i]))
    : B.sampled;
  const bind = Math.min(1, u / be);
  const strands = knot.strands.map((st, i) => {
    const sm = sampled[i];
    return { id: st.id, pts: sm.pts, s: sm.s, visibleTo: i === mi ? bind * sm.length : sm.length,
      workFrom: typeof st.workFrom === 'number' ? st.workFrom : null };
  });
  // Über/Unter: in der Bindephase ist die Form fest, die Kreuzungen sind die der Ausgangsform. Beim Zuziehen wird
  // neu gesucht und jede Kreuzung der nächstgelegenen Ausgangskreuzung desselben Strangpaars zugeordnet.
  let crossings;
  if (zu > 0) {
    const bl = B.sampled.map((x) => x.length || 1), cl = sampled.map((x) => x.length || 1);
    crossings = findCrossings(sampled).map((c) => {
      let best = -1, bd = Infinity;
      B.crossings.forEach((o, k) => {
        if (o.a.strand !== c.a.strand || o.b.strand !== c.b.strand) return;
        const d = Math.abs(o.a.s / bl[o.a.strand] - c.a.s / cl[c.a.strand]) + Math.abs(o.b.s / bl[o.b.strand] - c.b.s / cl[c.b.strand]);
        if (d < bd) { bd = d; best = k; }
      });
      return { ...c, over: best < 0 ? true : knot.over[best] !== false };
    });
  } else {
    crossings = B.crossings.map((c, k) => ({ ...c, over: knot.over[k] !== false }));
  }
  for (const c of crossings) c.visible = c.a.s <= strands[c.a.strand].visibleTo && c.b.s <= strands[c.b.strand].visibleTo;
  let tip = null;
  if (mi >= 0) {
    const st = strands[mi];
    const L = st.s[st.s.length - 1] || 0;
    const p = sAt(st, st.visibleTo);
    const back = sAt(st, Math.max(0, Math.min(st.visibleTo, L) - 2)), ahead = sAt(st, Math.min(L, st.visibleTo + 2));
    let dx = ahead.x - back.x, dy = ahead.y - back.y;
    const n = Math.hypot(dx, dy);
    if (n > 1e-9) { dx /= n; dy /= n; } else { dx = 1; dy = 0; }
    tip = { x: p.x, y: p.y, dx, dy };
  }
  return { strands, crossings, tip };
}

// ---------- Zeichnen ----------

const r1 = (n) => Math.round(n * 10) / 10;
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function pathD(st, s0, s1) {
  if (s1 - s0 <= 1e-6 || st.pts.length < 2) return '';
  const a = sAt(st, s0), b = sAt(st, s1);
  let d = 'M' + r1(a.x) + ' ' + r1(a.y);
  for (let k = a.i + 1; k <= b.i; k++) d += 'L' + r1(st.pts[k][0]) + ' ' + r1(st.pts[k][1]);
  return d + 'L' + r1(b.x) + ' ' + r1(b.y);
}

// Abschnitte [s0,s1] eines Strangs in Kernklassen zerlegt (jetzt > erledigt > Arbeitsende > Seil).
function coreRuns(fs, isMoving, s0, s1, ctx) {
  const L = fs.s[fs.s.length - 1] || 0;
  const cuts = [s0, s1];
  const work = fs.workFrom == null ? Infinity : fs.workFrom * L;
  if (work > s0 && work < s1) cuts.push(work);
  if (isMoving && ctx.hl) for (const v of ctx.hl) if (v > s0 && v < s1) cuts.push(v);
  cuts.sort((x, y) => x - y);
  const runs = [];
  for (let k = 0; k < cuts.length - 1; k++) {
    const a = cuts[k], b = cuts[k + 1];
    if (b - a <= 1e-6) continue;
    const m = (a + b) / 2;
    let cls = m >= work ? 'kn-rope-work' : 'kn-rope';
    if (isMoving && ctx.hl) {
      if (m >= ctx.hl[0] && m <= ctx.hl[1]) cls = 'kn-rope-now';
      else if (ctx.pale && m < ctx.hl[0]) cls = 'kn-rope-done';
    }
    const last = runs[runs.length - 1];
    if (last && last.cls === cls) last.b = b; else runs.push({ cls, a, b });
  }
  return runs;
}

// Stück [s0,s1] als Band: Kante, darauf der Kern. Stöße innerhalb des Seils (hinten/vorn, Klassenwechsel,
// Flickstück) bekommen flache Enden — runde Enden malten dort die Kante über den eigenen Kern. Nur die echten
// Seilenden (Anfang, sichtbares Ende) werden als Punkt mit runder Kappe gezeichnet. `ext` verlängert den Kern über
// die Kante hinaus (Flickstück: deckt die Naht am Stoß ab).
function piece(fs, isMoving, s0, s1, ctx, ext = 0) {
  const d = pathD(fs, s0, s1);
  if (!d) return '';
  const vis = Math.min(fs.visibleTo, fs.s[fs.s.length - 1] || 0);
  const attrs = (w, cap) => '" fill="none" stroke-width="' + w + '" stroke-linecap="' + cap + '" stroke-linejoin="round"/>';
  const c0 = Math.max(0, s0 - ext), c1 = Math.min(vis, s1 + ext);
  const runs = coreRuns(fs, isMoving, c0, c1, ctx);
  const dot = (s) => { const p = sAt(fs, s); return 'M' + r1(p.x) + ' ' + r1(p.y) + 'l0 0'; };
  const ends = [];
  if (s0 <= 1e-6) ends.push([0, runs.length ? runs[0].cls : 'kn-rope']);
  if (s1 >= vis - 1e-6) ends.push([vis, runs.length ? runs[runs.length - 1].cls : 'kn-rope']);
  // erst alle Kanten (auch die runden Endpunkte), dann alle Kerne — sonst malte die Kantenkappe über den Kern
  let out = '<path class="kn-rope-edge" d="' + d + attrs(EDGE_W, 'butt');
  for (const [s] of ends) out += '<path class="kn-rope-edge" d="' + dot(s) + attrs(EDGE_W, 'round');
  for (const r of runs) out += '<path class="' + r.cls + '" d="' + pathD(fs, r.a, r.b) + attrs(CORE_W, 'butt');
  for (const [s, cls] of ends) out += '<path class="' + cls + '" d="' + dot(s) + attrs(CORE_W, 'round');
  return out;
}

// Sichtbarer Teil eines Strangs in „hinter den Objekten“ und „davor“ zerlegt.
function layerRanges(st, fs) {
  const L = fs.s[fs.s.length - 1] || 0, vis = Math.min(fs.visibleTo, L);
  const behind = (st.behind || []).map(([a, b]) => [Math.max(0, a * L), Math.min(vis, b * L)]).filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  const front = [];
  let at = 0;
  for (const [a, b] of behind) { if (a > at) front.push([at, a]); at = Math.max(at, b); }
  if (vis > at) front.push([at, vis]);
  return { behind, front };
}

const inRanges = (s, rs) => rs.some(([a, b]) => s >= a && s <= b);

// Halbe Länge des Flickstücks: das untere Band (halbe Breite EDGE_W/2) belegt auf dem oberen Durchgang
// (EDGE_W/2)/sin θ — bei flachem Winkel θ deutlich mehr als bei einer rechtwinkligen Kreuzung. Fester Wert ließ
// unter ~36° die Kante, unter ~26° den Kern des unteren Seils neben dem Flickstück durchscheinen.
function patchHalf(fr, c) {
  const dir = (p) => {
    const pts = fr.strands[p.strand].pts, a = pts[p.i], b = pts[p.i + 1] || a;
    const dx = b[0] - a[0], dy = b[1] - a[1], n = Math.hypot(dx, dy) || 1;
    return [dx / n, dy / n];
  };
  const [ax, ay] = dir(c.a), [bx, by] = dir(c.b);
  const sin = Math.abs(ax * by - ay * bx);
  return Math.min(PATCH_MAX, Math.max(PATCH, (EDGE_W / 2) / Math.max(sin, 0.2) + PATCH_EXT));
}

function object(o) {
  const type = String(o.type || '').replace(/[^a-z]/g, '') || 'obj';
  if (type === 'ring') {
    return '<ellipse class="kn-obj-ring" cx="' + r1(o.x + o.w / 2) + '" cy="' + r1(o.y + o.h / 2) + '" rx="' + r1(o.w / 2) + '" ry="' + r1(o.h / 2) + '"/>';
  }
  const rx = type === 'tree' ? Math.min(o.w, o.h) / 4 : type === 'line' ? 0 : Math.min(o.w, o.h) / 6;
  return '<rect class="kn-obj-' + type + '" x="' + r1(o.x) + '" y="' + r1(o.y) + '" width="' + r1(o.w) + '" height="' + r1(o.h) + '" rx="' + r1(rx) + '"/>';
}

function renderSvg(knot, fr, opts = {}) {
  const [w, h] = knot.viewBox || [100, 100];
  const mi = movingIndex(knot);
  const ctx = { pale: !!opts.pale, hl: null };
  if (Array.isArray(opts.highlight) && mi >= 0) {
    const be = bindEnd(knot), L = fr.strands[mi].s[fr.strands[mi].s.length - 1] || 0;
    const toS = (v) => Math.min(1, clamp01(v) / be) * L;
    const a = toS(opts.highlight[0]), b = toS(opts.highlight[1]);
    ctx.hl = [Math.min(a, b), Math.max(a, b)];
  }
  const layers = knot.strands.map((st, i) => layerRanges(st, fr.strands[i]));
  let back = '', front = '', backPatch = '', frontPatch = '';
  knot.strands.forEach((st, i) => {
    for (const [a, b] of layers[i].behind) back += piece(fr.strands[i], i === mi, a, b, ctx);
    for (const [a, b] of layers[i].front) front += piece(fr.strands[i], i === mi, a, b, ctx);
  });
  for (const c of fr.crossings) {
    if (!c.visible) continue;
    const top = c.over ? c.b : c.a;
    const fs = fr.strands[top.strand];
    const vis = Math.min(fs.visibleTo, fs.s[fs.s.length - 1] || 0);
    const half = patchHalf(fr, c);
    const g = '<g class="kn-patch" data-strand="' + top.strand + '" data-s="' + Math.round(top.s) + '">'
      + piece(fs, top.strand === mi, Math.max(0, top.s - half), Math.min(vis, top.s + half), ctx, PATCH_EXT) + '</g>';
    if (inRanges(top.s, layers[top.strand].behind)) backPatch += g; else frontPatch += g;
  }
  let arrow = '';
  if (opts.arrow && fr.tip) {
    const { x, y, dx, dy } = fr.tip, nx = -dy, ny = dx;
    const bx = x + dx * 4, by = y + dy * 4, ax = x + dx * 14, ay = y + dy * 14;
    arrow = '<path class="kn-arrow" d="M' + r1(bx + nx * 6) + ' ' + r1(by + ny * 6) + 'L' + r1(ax) + ' ' + r1(ay)
      + 'L' + r1(bx - nx * 6) + ' ' + r1(by - ny * 6) + 'Z"/>';
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + r1(w) + ' ' + r1(h) + '" role="img"><title>'
    + esc(opts.title == null ? '' : opts.title) + '</title>'
    + '<g class="kn-back">' + back + backPatch + '</g>'
    + '<g class="kn-objects">' + (knot.objects || []).map(object).join('') + '</g>'
    + '<g class="kn-front">' + front + frontPatch + '</g>' + arrow + '</svg>';
}

// ---------- Schritte ----------

function stepAt(knot, u) {
  const steps = knot.steps || [1];
  const v = clamp01(u);
  for (let i = 0; i < steps.length; i++) if (steps[i] >= v - 1e-9) return i;
  return steps.length - 1;
}

function uOfStep(knot, i) {
  const steps = knot.steps || [1];
  return steps[Math.min(steps.length - 1, Math.max(0, Math.round(Number(i) || 0)))];
}

/**
 * Schrittbild i: Stelle, Hervorhebung, blasser Rest, Pfeil. Der letzte Schritt eines Knotens mit Zuziehphase liegt
 * ganz in u > BIND_END — dort ist das Seil schon vollständig gelegt und eine Hervorhebung [BIND_END, 1] wäre leer.
 * Er hebt deshalb den ganzen bewegten Strang hervor (nichts blass, kein Pfeil): „jetzt alles festziehen“.
 * (Hier statt im Spieler, damit die öffentlichen Seiten dieselben Schrittbilder serverseitig zeichnen.)
 */
function stepView(knot, i) {
  const steps = knot.steps || [1];
  const k = Math.min(steps.length - 1, Math.max(0, Math.round(Number(i) || 0)));
  const a = k ? steps[k - 1] : 0, b = steps[k];
  const last = k === steps.length - 1;
  const tighten = (knot.strands || []).some(hasTight) && a >= BIND_END - 1e-9;
  if (tighten) return { u: b, highlight: [0, 1], pale: false, arrow: false, tighten: true };
  return { u: b, highlight: [a, b], pale: true, arrow: !last, tighten: false };
}

export { sampleStrand, findCrossings, frame, renderSvg, stepAt, uOfStep, stepView, BIND_END, PATCH };
